import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type LoginOptions, type VersionInfo } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ErrorNote, Spinner } from "../../components/ui";

/**
 * The sign-in screen: a two-column split, as in the prototype.
 *
 * The account picker exists because the local identity provider stands in for
 * Entra ID: it makes every role reachable without a directory. Under
 * `IDENTITY_PROVIDER=entra` the server returns no accounts and only the Entra
 * button is offered, so this list cannot survive into production by accident.
 */
export function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const {
    data: options,
    isLoading,
    error: optionsError,
  } = useQuery({
    queryKey: ["login-options"],
    queryFn: () => api.get<LoginOptions>("/auth/options"),
  });

  const { data: version } = useQuery({
    queryKey: ["version"],
    queryFn: () => api.get<VersionInfo>("/version"),
    staleTime: 5 * 60_000,
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-ink lg:grid-cols-[1.05fr_0.95fr]">
      <section className="flex flex-col justify-between p-8 lg:p-14">
        <img
          src="/assets/nxzen-primary.jpg"
          alt="nxzen"
          className="h-9 w-auto self-start rounded-chip"
        />

        <div className="max-w-xl py-12">
          <h1 className="font-display text-[42px] leading-[1.05] text-pearl lg:text-hero">
            Compendium, <span className="text-accent">rebuilt</span> with insight.
          </h1>
          <p className="mt-5 max-w-lg text-md leading-relaxed text-muted-soft">
            An AI-assisted document management system for SGN Operational
            Technology. Ask a question in plain English and get an answer grounded
            in the compendium, cited to the page it came from. SharePoint stays
            the system of record.
          </p>
          <div className="mt-7 flex flex-wrap gap-2">
            {[
              { label: "Grounded answers", colour: "#8DE971" },
              { label: "SharePoint sync", colour: "#AD96DC" },
              { label: "Hybrid search", colour: "#74D1EA" },
            ].map((chip) => (
              <span
                key={chip.label}
                className="rounded-full border px-3 py-1 text-sm font-bold"
                style={{ color: chip.colour, borderColor: `${chip.colour}55` }}
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>

        <p className="text-2xs text-muted">
          Every answer cites its source · Permissions applied at retrieval, not
          after · Seven-year audit trail
        </p>
      </section>

      <section className="flex items-center justify-center bg-night-900 p-8 lg:p-14">
        <div className="w-full max-w-sm">
          <div className="eyebrow mb-2">Sign in</div>
          <h2 className="mb-6 font-display text-[32px] text-pearl">Welcome back</h2>

          {/* Beside the form, not in the panel opposite it. Somebody signing in
              is looking here, and a disclosure placed where nobody is looking
              is decoration. `/version` is unauthenticated for exactly this
              reason: the warning has to be readable before the credentials
              are. */}
          {version?.dataResidencyWarning && (
            <p className="mb-5 rounded-soft border border-[#5C4F1A] bg-[#2A2412] px-3 py-2.5 text-2xs leading-[1.6] text-[#E8D98A]">
              <span className="font-bold">
                Content is leaving the SGN tenancy.
              </span>{" "}
              Questions and retrieved document text are sent to{" "}
              {version.providers.llm}. Specification sections 2.1 and 9 require
              that they do not.
            </p>
          )}

          {isLoading && <Spinner label="Checking sign-in options…" />}

          {/* Every control below is gated on `options`. Without this the panel
              rendered as a heading over empty space when the request failed,
              which reads as a broken page rather than an unreachable API. */}
          {optionsError !== null && optionsError !== undefined && (
            <ErrorNote error={optionsError} />
          )}

          {options?.externalLogin && (
            <button
              className="btn-accent mb-5 w-full !py-2.5"
              onClick={() =>
                setError(
                  new Error(
                    "Entra ID sign-in is configured on the server but this proof of concept does not embed MSAL. Sign in with your email and password instead.",
                  ),
                )
              }
            >
              {options.externalLabel}
            </button>
          )}

          {options?.passwordLogin && (
            <form onSubmit={submit} className="space-y-3">
              <label className="block">
                <span className="eyebrow mb-1.5 block !text-muted">Work email</span>
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@sgn.co.uk"
                  className="field !border-night-500 !bg-night-700 !text-pearl focus:!border-muted"
                />
              </label>
              <label className="block">
                <span className="eyebrow mb-1.5 block !text-muted">Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="field !border-night-500 !bg-night-700 !text-pearl focus:!border-muted"
                />
              </label>

              {error !== null && <ErrorNote error={error} />}

              <button type="submit" className="btn-accent w-full !py-2.5" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          )}

          {/* The account list is gone. It named four real people, their roles
              and what each could do, on an unauthenticated page -- and told
              anyone reading it that they all share one password held in an
              environment variable. Convenient for a demo, and a directory of
              targets with a hint attached for anybody else.

              People type their own credentials now. `/auth/options` still
              reports whether password sign-in is available, which is what
              gates the form above; it is only the roster that is not drawn. */}
          {options?.passwordLogin && (
            <p className="mt-6 text-2xs leading-relaxed text-muted">
              Roles come from Entra ID group membership. New starters default to
              System Engineer, which is read-only.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
